/**
 * Publisher Manifest Section
 *
 * Configures and monitors publisher manifest integration for deployment verification.
 * Allows setting up manifest sources, viewing fetch history, and monitoring matched records.
 *
 * Date: 2026-02-13
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import {
  Plus,
  Settings,
  Trash2,
  Play,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Globe,
  FileText,
  ExternalLink,
  AlertTriangle,
  FlaskConical,
} from 'lucide-react';

// Types
interface ManifestConfig {
  id: string;
  site_id: string;
  name: string;
  manifest_url: string;
  manifest_format: 'tsv' | 'csv' | 'json';
  column_mapping: Record<string, number>;
  url_id_pattern: string;
  country_code_pattern: string;
  fetch_frequency: 'hourly' | 'daily' | 'manual';
  status: 'active' | 'paused' | 'error';
  last_fetched_at: string | null;
  last_fetch_error: string | null;
  created_at: string;
  updated_at: string;
}

interface FetchLog {
  id: string;
  config_id: string;
  fetch_started_at: string;
  fetch_completed_at: string | null;
  status: 'running' | 'success' | 'failed';
  records_fetched: number;
  records_new: number;
  records_updated: number;
  records_matched: number;
  error_message: string | null;
}

interface ManifestStats {
  total_records: number;
  matched_products: number;
  unique_products: number;
  countries: number;
  confirmed_urls: number;
  earliest_publication: string | null;
  latest_publication: string | null;
  config_count: number;
  active_configs: number;
}

interface ProductCoverage {
  matched_aeo_id: string;
  extracted_product_id: string;
  original_url: string;
  countries_deployed: number;
  country_codes: string[];
  deployed_urls: string[];
  first_deployed_at: string;
  last_deployed_at: string;
}

interface TestResult {
  valid: boolean;
  error: string | null;
  format: {
    requested: string;
    detected: string;
    match: boolean;
  };
  stats: {
    totalRecords: number;
    fileSize: number;
    fileSizeFormatted: string;
    fetchTimeMs: number;
    columnsPerRow: number;
    columnConsistent: boolean;
    columnVariations: number[];
  };
  preview: Array<{
    line: number;
    columns: number;
    raw: string[];
    extracted: {
      itemGroupId: string | null;
      sku: string | null;
      timestamp: string | null;
      url: string | null;
      productId: string | null;
      countryCode: string | null;
    };
  }>;
}

interface Props {
  siteId: string;
}

export default function PublisherManifestSection({ siteId }: Props) {
  // Config state
  const [configs, setConfigs] = useState<ManifestConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  // Stats state
  const [stats, setStats] = useState<ManifestStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch logs state
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [fetchLogs, setFetchLogs] = useState<FetchLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Product coverage state
  const [coverage, setCoverage] = useState<ProductCoverage[]>([]);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ManifestConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [fetchingManifest, setFetchingManifest] = useState<string | null>(null);

  // Test URL state
  const [testingUrl, setTestingUrl] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  // Form state for new/edit config
  const [formData, setFormData] = useState({
    name: '',
    manifestUrl: '',
    manifestFormat: 'tsv' as 'tsv' | 'csv' | 'json',
    fetchFrequency: 'daily' as 'hourly' | 'daily' | 'manual',
    urlIdPattern: '/(\\d+)/p',
    countryCodePattern: '/bikeinn/([a-z]{2})/',
  });

  // Load configs
  const loadConfigs = useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifests?siteId=${siteId}`);
      if (!response.ok) throw new Error('Failed to load configs');
      const data = await response.json();
      if (data.success) {
        setConfigs(data.data);
        // Auto-select first config if available
        if (data.data.length > 0 && !selectedConfigId) {
          setSelectedConfigId(data.data[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading configs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load publisher manifest configs',
        variant: 'destructive',
      });
    } finally {
      setLoadingConfigs(false);
    }
  }, [siteId, selectedConfigId]);

  // Load stats
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifest-stats?siteId=${siteId}`);
      if (!response.ok) throw new Error('Failed to load stats');
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  }, [siteId]);

  // Load fetch logs for selected config
  const loadFetchLogs = useCallback(async (configId: string) => {
    setLoadingLogs(true);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifests/${configId}/logs?limit=10`);
      if (!response.ok) throw new Error('Failed to load logs');
      const data = await response.json();
      if (data.success) {
        setFetchLogs(data.data);
      }
    } catch (error) {
      console.error('Error loading fetch logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  // Load product coverage
  const loadCoverage = useCallback(async () => {
    setLoadingCoverage(true);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifest-coverage?siteId=${siteId}&limit=20`);
      if (!response.ok) throw new Error('Failed to load coverage');
      const data = await response.json();
      if (data.success) {
        setCoverage(data.data);
      }
    } catch (error) {
      console.error('Error loading coverage:', error);
    } finally {
      setLoadingCoverage(false);
    }
  }, [siteId]);

  // Create config
  const createConfig = async () => {
    setSavingConfig(true);
    try {
      const response = await authFetch('/crawl-intelligence/publisher-manifests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          name: formData.name,
          manifestUrl: formData.manifestUrl,
          manifestFormat: formData.manifestFormat,
          fetchFrequency: formData.fetchFrequency,
          urlIdPattern: formData.urlIdPattern,
          countryCodePattern: formData.countryCodePattern,
        }),
      });

      if (!response.ok) throw new Error('Failed to create config');

      toast({
        title: 'Success',
        description: 'Publisher manifest config created',
      });

      setIsAddDialogOpen(false);
      resetForm();
      loadConfigs();
    } catch (error) {
      console.error('Error creating config:', error);
      toast({
        title: 'Error',
        description: 'Failed to create config',
        variant: 'destructive',
      });
    } finally {
      setSavingConfig(false);
    }
  };

  // Update config
  const updateConfig = async () => {
    if (!editingConfig) return;

    setSavingConfig(true);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifests/${editingConfig.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          manifestUrl: formData.manifestUrl,
          manifestFormat: formData.manifestFormat,
          fetchFrequency: formData.fetchFrequency,
          urlIdPattern: formData.urlIdPattern,
          countryCodePattern: formData.countryCodePattern,
        }),
      });

      if (!response.ok) throw new Error('Failed to update config');

      toast({
        title: 'Success',
        description: 'Publisher manifest config updated',
      });

      setIsEditDialogOpen(false);
      setEditingConfig(null);
      resetForm();
      loadConfigs();
    } catch (error) {
      console.error('Error updating config:', error);
      toast({
        title: 'Error',
        description: 'Failed to update config',
        variant: 'destructive',
      });
    } finally {
      setSavingConfig(false);
    }
  };

  // Delete config
  const deleteConfig = async (configId: string) => {
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifests/${configId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete config');

      toast({
        title: 'Success',
        description: 'Publisher manifest config deleted',
      });

      if (selectedConfigId === configId) {
        setSelectedConfigId(null);
        setFetchLogs([]);
      }
      loadConfigs();
      loadStats();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete config',
        variant: 'destructive',
      });
    }
  };

  // Trigger manual fetch (async - polls for completion)
  const triggerFetch = async (configId: string) => {
    setFetchingManifest(configId);
    try {
      const response = await authFetch(`/crawl-intelligence/publisher-manifests/${configId}/fetch`, {
        method: 'POST',
      });

      if (!response.ok) throw new Error('Failed to trigger fetch');

      const data = await response.json();

      if (data.success && data.status === 'processing') {
        toast({
          title: 'Fetch Started',
          description: 'Processing manifest in background. This may take 1-2 minutes...',
        });

        // Poll for completion every 3 seconds
        const pollInterval = setInterval(async () => {
          try {
            const logsResponse = await authFetch(`/crawl-intelligence/publisher-manifests/${configId}/logs?limit=1`);
            if (logsResponse.ok) {
              const logsData = await logsResponse.json();
              if (logsData.success && logsData.data.length > 0) {
                const latestLog = logsData.data[0];
                // Check if the latest log is complete (success or failed)
                if (latestLog.status === 'success' || latestLog.status === 'failed') {
                  clearInterval(pollInterval);
                  setFetchingManifest(null);

                  if (latestLog.status === 'success') {
                    toast({
                      title: 'Fetch Complete',
                      description: `Fetched ${latestLog.records_fetched} records: ${latestLog.records_new} new, ${latestLog.records_updated} updated, ${latestLog.records_matched} matched`,
                    });
                  } else {
                    toast({
                      title: 'Fetch Failed',
                      description: latestLog.error_message || 'Unknown error occurred',
                      variant: 'destructive',
                    });
                  }

                  // Refresh all data
                  loadConfigs();
                  loadStats();
                  loadFetchLogs(configId);
                  loadCoverage();
                }
              }
            }
          } catch (pollError) {
            console.error('Error polling fetch status:', pollError);
          }
        }, 3000);

        // Safety timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (fetchingManifest === configId) {
            setFetchingManifest(null);
            toast({
              title: 'Fetch Timeout',
              description: 'Processing is taking longer than expected. Check logs for status.',
              variant: 'destructive',
            });
          }
        }, 300000);

      } else if (data.success && data.data) {
        // Synchronous response (backwards compatibility)
        const stats = data.data;
        toast({
          title: 'Fetch Complete',
          description: `Fetched ${stats.recordsFetched} records: ${stats.recordsNew} new, ${stats.recordsUpdated} updated, ${stats.recordsMatched} matched`,
        });

        loadConfigs();
        loadStats();
        if (selectedConfigId === configId) {
          loadFetchLogs(configId);
        }
        loadCoverage();
        setFetchingManifest(null);
      }
    } catch (error) {
      console.error('Error triggering fetch:', error);
      toast({
        title: 'Error',
        description: 'Failed to trigger fetch',
        variant: 'destructive',
      });
      setFetchingManifest(null);
    }
  };

  // Test manifest URL
  const testManifestUrl = async () => {
    if (!formData.manifestUrl) {
      toast({
        title: 'Error',
        description: 'Please enter a manifest URL to test',
        variant: 'destructive',
      });
      return;
    }

    setTestingUrl(true);
    setTestResult(null);

    try {
      const response = await authFetch('/crawl-intelligence/publisher-manifests/test-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          manifestUrl: formData.manifestUrl,
          manifestFormat: formData.manifestFormat,
          urlIdPattern: formData.urlIdPattern,
          countryCodePattern: formData.countryCodePattern,
        }),
      });

      if (!response.ok) throw new Error('Failed to test URL');

      const data = await response.json();
      if (data.success) {
        setTestResult(data.data);
        toast({
          title: data.data.valid ? 'URL Valid' : 'URL Invalid',
          description: data.data.valid
            ? `Found ${data.data.stats.totalRecords.toLocaleString()} records`
            : data.data.error,
          variant: data.data.valid ? 'default' : 'destructive',
        });
      }
    } catch (error) {
      console.error('Error testing URL:', error);
      toast({
        title: 'Error',
        description: 'Failed to test manifest URL',
        variant: 'destructive',
      });
    } finally {
      setTestingUrl(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (config: ManifestConfig) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      manifestUrl: config.manifest_url,
      manifestFormat: config.manifest_format,
      fetchFrequency: config.fetch_frequency,
      urlIdPattern: config.url_id_pattern || '/(\\d+)/p',
      countryCodePattern: config.country_code_pattern || '/bikeinn/([a-z]{2})/',
    });
    setTestResult(null);
    setIsEditDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      manifestUrl: '',
      manifestFormat: 'tsv',
      fetchFrequency: 'daily',
      urlIdPattern: '/(\\d+)/p',
      countryCodePattern: '/bikeinn/([a-z]{2})/',
    });
    setTestResult(null);
  };

  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Load data on mount
  useEffect(() => {
    loadConfigs();
    loadStats();
    loadCoverage();
  }, [loadConfigs, loadStats, loadCoverage]);

  // Load fetch logs when config is selected
  useEffect(() => {
    if (selectedConfigId) {
      loadFetchLogs(selectedConfigId);
    }
  }, [selectedConfigId, loadFetchLogs]);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Configs</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">
                  {stats?.active_configs || 0}/{stats?.config_count || 0}
                </span>
              </div>
            )}
            <p className="text-xs text-muted-foreground">active</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Records</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-purple-500" />
                <span className="text-2xl font-bold">
                  {(stats?.total_records || 0).toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Matched Products</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-2xl font-bold">
                  {(stats?.matched_products || 0).toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Confirmed URLs</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-cyan-500" />
                <span className="text-2xl font-bold">
                  {(stats?.confirmed_urls || 0).toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Unique Products</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-500" />
                <span className="text-2xl font-bold">
                  {(stats?.unique_products || 0).toLocaleString()}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Countries</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingStats ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-indigo-500" />
                <span className="text-2xl font-bold">{stats?.countries || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Configs Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Manifest Configurations</CardTitle>
              <CardDescription>
                Configure publisher manifest sources for deployment verification
              </CardDescription>
            </div>
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Manifest
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Publisher Manifest</DialogTitle>
                  <DialogDescription>
                    Configure a new manifest source for deployment verification
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., BikeInn US Products"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="manifestUrl">Manifest URL</Label>
                    <div className="flex gap-2">
                      <Input
                        id="manifestUrl"
                        placeholder="https://storage.googleapis.com/.../manifest.txt"
                        value={formData.manifestUrl}
                        onChange={(e) => {
                          setFormData({ ...formData, manifestUrl: e.target.value });
                          setTestResult(null);
                        }}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={testManifestUrl}
                        disabled={testingUrl || !formData.manifestUrl}
                      >
                        {testingUrl ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <FlaskConical className="h-4 w-4" />
                        )}
                        <span className="ml-2">Test</span>
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label>Format</Label>
                      <Select
                        value={formData.manifestFormat}
                        onValueChange={(v) => setFormData({ ...formData, manifestFormat: v as 'tsv' | 'csv' | 'json' })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tsv">TSV</SelectItem>
                          <SelectItem value="csv">CSV</SelectItem>
                          <SelectItem value="json">JSON</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label>Frequency</Label>
                      <Select
                        value={formData.fetchFrequency}
                        onValueChange={(v) => setFormData({ ...formData, fetchFrequency: v as 'hourly' | 'daily' | 'manual' })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily (6 AM)</SelectItem>
                          <SelectItem value="hourly">Hourly (:45)</SelectItem>
                          <SelectItem value="manual">Manual only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="urlIdPattern">Product ID Pattern (regex)</Label>
                    <Input
                      id="urlIdPattern"
                      placeholder="/(\\d+)/p"
                      value={formData.urlIdPattern}
                      onChange={(e) => setFormData({ ...formData, urlIdPattern: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Regex to extract product ID from URL. Default extracts numbers before /p
                    </p>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="countryCodePattern">Country Code Pattern (regex)</Label>
                    <Input
                      id="countryCodePattern"
                      placeholder="/bikeinn/([a-z]{2})/"
                      value={formData.countryCodePattern}
                      onChange={(e) => setFormData({ ...formData, countryCodePattern: e.target.value })}
                    />
                    <p className="text-xs text-muted-foreground">
                      Regex to extract country/language code from URL
                    </p>
                  </div>

                  {/* Test Results */}
                  {testResult && (
                    <div className={`p-3 rounded-lg border ${testResult.valid ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {testResult.valid ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        )}
                        <span className={`font-medium ${testResult.valid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                          {testResult.valid ? 'Valid Manifest' : 'Invalid Manifest'}
                        </span>
                      </div>

                      {testResult.error && (
                        <p className="text-sm text-red-600 dark:text-red-400 mb-2">{testResult.error}</p>
                      )}

                      {testResult.valid && (
                        <>
                          {/* Stats */}
                          <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                            <div className="bg-white dark:bg-slate-800 p-2 rounded">
                              <div className="text-muted-foreground">Records</div>
                              <div className="font-bold">{testResult.stats.totalRecords.toLocaleString()}</div>
                            </div>
                            <div className="bg-white dark:bg-slate-800 p-2 rounded">
                              <div className="text-muted-foreground">File Size</div>
                              <div className="font-bold">{testResult.stats.fileSizeFormatted}</div>
                            </div>
                            <div className="bg-white dark:bg-slate-800 p-2 rounded">
                              <div className="text-muted-foreground">Fetch Time</div>
                              <div className="font-bold">{testResult.stats.fetchTimeMs}ms</div>
                            </div>
                          </div>

                          {/* Format Detection */}
                          {!testResult.format.match && (
                            <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 mb-2">
                              <AlertTriangle className="h-3 w-3" />
                              Format mismatch: detected {testResult.format.detected.toUpperCase()}, expected {testResult.format.requested.toUpperCase()}
                            </div>
                          )}

                          {/* Preview Table */}
                          <div className="text-xs">
                            <div className="font-medium mb-1">Preview (first {testResult.preview.length} rows):</div>
                            <div className="max-h-40 overflow-auto bg-white dark:bg-slate-800 rounded border dark:border-slate-700">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                                  <tr>
                                    <th className="p-1 text-left">#</th>
                                    <th className="p-1 text-left">Group ID</th>
                                    <th className="p-1 text-left">SKU</th>
                                    <th className="p-1 text-left">Product ID</th>
                                    <th className="p-1 text-left">Country</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {testResult.preview.map((row, idx) => (
                                    <tr key={idx} className="border-t dark:border-slate-700">
                                      <td className="p-1 text-muted-foreground">{row.line}</td>
                                      <td className="p-1 font-mono">{row.extracted.itemGroupId || '-'}</td>
                                      <td className="p-1 font-mono">{row.extracted.sku || '-'}</td>
                                      <td className="p-1 font-mono text-green-600 dark:text-green-400">{row.extracted.productId || '-'}</td>
                                      <td className="p-1 font-mono text-blue-600 dark:text-blue-400">{row.extracted.countryCode?.toUpperCase() || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={createConfig} disabled={savingConfig || !formData.name || !formData.manifestUrl}>
                    {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Create
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loadingConfigs ? (
            <div className="space-y-2">
              {[...Array(2)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : configs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No manifest configurations yet. Click "Add Manifest" to create one.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>URL</TableHead>
                  <TableHead>Format</TableHead>
                  <TableHead>Frequency</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Fetched</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((config) => (
                  <TableRow
                    key={config.id}
                    className={selectedConfigId === config.id ? 'bg-muted/50' : ''}
                    onClick={() => setSelectedConfigId(config.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell className="font-medium">{config.name}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[200px] truncate" title={config.manifest_url}>
                      {config.manifest_url.substring(0, 50)}...
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{config.manifest_format.toUpperCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{config.fetch_frequency}</Badge>
                    </TableCell>
                    <TableCell>
                      {config.status === 'active' ? (
                        <Badge variant="default">Active</Badge>
                      ) : config.status === 'error' ? (
                        <Badge variant="destructive" title={config.last_fetch_error || ''}>
                          Error
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Paused</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(config.last_fetched_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            triggerFetch(config.id);
                          }}
                          disabled={fetchingManifest === config.id}
                          title="Fetch now"
                        >
                          {fetchingManifest === config.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditDialog(config);
                          }}
                          title="Edit"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => e.stopPropagation()}
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Configuration</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{config.name}"? This will also delete all associated records.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteConfig(config.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Publisher Manifest</DialogTitle>
            <DialogDescription>
              Update manifest configuration
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-manifestUrl">Manifest URL</Label>
              <div className="flex gap-2">
                <Input
                  id="edit-manifestUrl"
                  value={formData.manifestUrl}
                  onChange={(e) => {
                    setFormData({ ...formData, manifestUrl: e.target.value });
                    setTestResult(null);
                  }}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={testManifestUrl}
                  disabled={testingUrl || !formData.manifestUrl}
                >
                  {testingUrl ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FlaskConical className="h-4 w-4" />
                  )}
                  <span className="ml-2">Test</span>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Format</Label>
                <Select
                  value={formData.manifestFormat}
                  onValueChange={(v) => setFormData({ ...formData, manifestFormat: v as 'tsv' | 'csv' | 'json' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tsv">TSV</SelectItem>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Frequency</Label>
                <Select
                  value={formData.fetchFrequency}
                  onValueChange={(v) => setFormData({ ...formData, fetchFrequency: v as 'hourly' | 'daily' | 'manual' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily (6 AM)</SelectItem>
                    <SelectItem value="hourly">Hourly (:45)</SelectItem>
                    <SelectItem value="manual">Manual only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-urlIdPattern">Product ID Pattern</Label>
              <Input
                id="edit-urlIdPattern"
                value={formData.urlIdPattern}
                onChange={(e) => setFormData({ ...formData, urlIdPattern: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-countryCodePattern">Country Code Pattern</Label>
              <Input
                id="edit-countryCodePattern"
                value={formData.countryCodePattern}
                onChange={(e) => setFormData({ ...formData, countryCodePattern: e.target.value })}
              />
            </div>

            {/* Test Results in Edit Dialog */}
            {testResult && (
              <div className={`p-3 rounded-lg border ${testResult.valid ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {testResult.valid ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                  )}
                  <span className={`font-medium ${testResult.valid ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                    {testResult.valid ? 'Valid Manifest' : 'Invalid Manifest'}
                  </span>
                </div>

                {testResult.error && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">{testResult.error}</p>
                )}

                {testResult.valid && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-white dark:bg-slate-800 p-2 rounded">
                      <div className="text-muted-foreground">Records</div>
                      <div className="font-bold">{testResult.stats.totalRecords.toLocaleString()}</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-2 rounded">
                      <div className="text-muted-foreground">File Size</div>
                      <div className="font-bold">{testResult.stats.fileSizeFormatted}</div>
                    </div>
                    <div className="bg-white dark:bg-slate-800 p-2 rounded">
                      <div className="text-muted-foreground">Columns</div>
                      <div className="font-bold">{testResult.stats.columnsPerRow}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={updateConfig} disabled={savingConfig}>
              {savingConfig ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fetch Logs Section */}
      {selectedConfigId && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-lg">Recent Fetches</CardTitle>
                <CardDescription>
                  Fetch history for {configs.find(c => c.id === selectedConfigId)?.name}
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => loadFetchLogs(selectedConfigId)}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingLogs ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : fetchLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No fetch history yet. Click the play button to fetch the manifest.
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Started</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Fetched</TableHead>
                    <TableHead className="text-right">New</TableHead>
                    <TableHead className="text-right">Updated</TableHead>
                    <TableHead className="text-right">Matched</TableHead>
                    <TableHead>Duration</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fetchLogs.map((log) => {
                    const duration = log.fetch_completed_at
                      ? Math.round((new Date(log.fetch_completed_at).getTime() - new Date(log.fetch_started_at).getTime()) / 1000)
                      : null;

                    return (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm">
                          {formatDate(log.fetch_started_at)}
                        </TableCell>
                        <TableCell>
                          {log.status === 'success' ? (
                            <Badge variant="default">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Success
                            </Badge>
                          ) : log.status === 'failed' ? (
                            <Badge variant="destructive" title={log.error_message || ''}>
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                              Running
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {log.records_fetched.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          +{log.records_new.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-blue-600">
                          {log.records_updated.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-purple-600">
                          {log.records_matched.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {duration !== null ? `${duration}s` : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Product Coverage Section */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-lg">Multi-Market Coverage</CardTitle>
              <CardDescription>
                Products deployed across multiple country URLs
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={loadCoverage}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingCoverage ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCoverage ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : coverage.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No matched products yet. Fetch a manifest to see coverage.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product ID</TableHead>
                  <TableHead className="text-center">Markets</TableHead>
                  <TableHead>Countries</TableHead>
                  <TableHead>First Deployed</TableHead>
                  <TableHead>Last Deployed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coverage.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-mono text-sm">
                      {item.extracted_product_id}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{item.countries_deployed}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {item.country_codes.slice(0, 8).map((code) => (
                          <Badge key={code} variant="outline" className="text-xs">
                            {code.toUpperCase()}
                          </Badge>
                        ))}
                        {item.country_codes.length > 8 && (
                          <Badge variant="outline" className="text-xs">
                            +{item.country_codes.length - 8}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(item.first_deployed_at)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatRelativeTime(item.last_deployed_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
